import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { DisbursementMutationDto, DisbursementOverviewDto } from '../../../config/registrationConfig'
import ModifyRegistrationRow, {
    ModifyRegistrationColumnProps,
    ModifyRegistrationColumns,
    ModifyRegistrationRowProps,
} from '../Registration/ModifyRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { multiplyRate } from '../../../helpers/numberHelper'
import { DisbursementListProps } from './DisbursementList'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import { makeStyles } from '@fluentui/react-components'
import DisbursementTypeCombobox, { DisbursementTypeComboboxProps } from '../../Combobox/DisbursementTypeCombobox'
import useDisbursementTypes from '../../../hooks/useDisbursementTypes'
import { MoneyDto, VatType } from '../../../config/i18nConfig'
import useVatTypes from '../../../hooks/useVatTypes'
import FormikField from '../../Formik/FormikField'

const useStyles = makeStyles({
    vat: {
        width: '96px',
        minWidth: 'auto',
    },
    vatInput: {
        width: '63px',
    },
    type: {
        width: '120px',
        minWidth: 'auto',
    },
    typeInput: {
        width: '86px',
    },
})

type Props = ModifyRegistrationRowProps<DisbursementOverviewDto, DisbursementMutationDto, DisbursementListProps>
type ColumnProps = ModifyRegistrationColumnProps<DisbursementListProps>

function Columns(props: ColumnProps): ReactElement {
    const styles = useStyles()
    const { values, setFieldValue } = useFormikContext<DisbursementMutationDto>()
    const context = useFormikContext<DisbursementMutationDto>()
    const { disbursementTypes, getDisbursementType } = useDisbursementTypes()
    const { getVatType } = useVatTypes()
    const vatType = useMemo<VatType>(() => getVatType(values.vatType), [values.vatType])
    const total = useMemo<MoneyDto>(
        () =>
            multiplyRate((vatType?.percentage ?? 0) / 100 + 1, {
                amount: values.rate,
                currency: values.currency,
            }),
        [values.rate, vatType],
    )

    switch (props.column.key) {
        case 'type':
            return (
                <FormikField<DisbursementTypeComboboxProps>
                    context={context}
                    fieldName={'type'}
                    component={DisbursementTypeCombobox}
                    componentProps={{
                        size: 'small',
                        required: disbursementTypes.length > 0,
                        selectedOptions: [values.type],
                        onOptionSelect: (_, data) => {
                            const disbursementType = getDisbursementType(data.selectedOptions?.[0])
                            setFieldValue('type', data.selectedOptions?.[0])
                                .then(() => setFieldValue('vatType', disbursementType?.vatType))
                                .then(() => setFieldValue('description', values.description || disbursementType?.name))
                        },
                        placeholderPrefixKey: '',
                        placeholderKey: '',
                        className: styles.type,
                        input: { className: styles.typeInput },
                    }}
                />
            )
        case 'vat':
            return (
                <FormikField<VatTypeComboboxProps>
                    context={context}
                    fieldName={'vatType'}
                    component={VatTypeCombobox}
                    componentProps={{
                        size: 'small',
                        required: true,
                        selectedOptions: [values.vatType],
                        onOptionSelect: (_, data) => {
                            setFieldValue('vatType', data.selectedOptions?.[0])
                        },
                        placeholderPrefixKey: '',
                        placeholderKey: '',
                        className: styles.vat,
                        input: { className: styles.vatInput },
                    }}
                />
            )
        case 'total':
            return <MoneyText value={total} align="right" />
    }
    return <ModifyRegistrationColumns<DisbursementMutationDto, DisbursementListProps> {...props} />
}

export default function ModifyDisbursementRow(props: Props): ReactElement {
    return (
        <ModifyRegistrationRow<DisbursementOverviewDto, DisbursementMutationDto, DisbursementListProps>
            {...props}
            columns={Columns}
        />
    )
}
